import { throwError } from "@/lib/throwError";
import { get, post } from "@/services/axiosUitils";

type UploadFilesParams = {
  folderName: string;
  files: File[];
  /**
   * When true (default), after POST `/file-utility/files/` succeeds, calls
   * GET `/file-utility/files/{id}/` for each returned file id and merges the
   * payload (e.g. `file_path`, `file_path_thumbnail`) into each item.
   */
  fetchDetailsAfterUpload?: boolean;
};

const normalizeUploadResponseToItems = (
  raw: unknown
): { items: unknown[]; wrap: (enriched: unknown[]) => unknown } => {
  if (Array.isArray(raw)) {
    return {
      items: raw,
      wrap: enriched => enriched,
    };
  }
  if (raw && typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.data)) {
      const data = r.data as unknown[];
      return {
        items: data,
        wrap: enriched => ({ ...r, data: enriched }),
      };
    }
    if (Array.isArray(r.results)) {
      const results = r.results as unknown[];
      return {
        items: results,
        wrap: enriched => ({ ...r, results: enriched }),
      };
    }
    const id = r.id;
    if (id !== undefined && id !== null && String(id).trim() !== "") {
      return {
        items: [raw],
        wrap: enriched => enriched[0] ?? raw,
      };
    }
  }
  return {
    items: [],
    wrap: enriched => enriched,
  };
};

const getEntryId = (item: unknown): string | null => {
  if (!item || typeof item !== "object") {
    return null;
  }
  const o = item as Record<string, unknown>;
  const id = o.id ?? (o.data as Record<string, unknown> | undefined)?.id;
  if (id === undefined || id === null) {
    return null;
  }
  const s = String(id).trim();
  return s || null;
};

class UploadService {
  uploadFiles = async ({
    folderName,
    files,
    fetchDetailsAfterUpload = true,
  }: UploadFilesParams): Promise<any> => {
    if (!folderName || !files) {
      throwError("Folder name and files are required", "UploadService");
    }
    const formData = new FormData();
    formData.append("folder_name", folderName);

    files.forEach(file => {
      formData.append("file_path", file);
    });

    const response = await post("/file-utility/files/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (!fetchDetailsAfterUpload) {
      return response;
    }

    return await this.enrichUploadResponseWithFileDetails(response);
  };

  getFileById = async ({ id }: { id: string }): Promise<any> => {
    return await get(`/file-utility/files/${id}/`);
  };

  private enrichUploadResponseWithFileDetails = async (
    raw: unknown
  ): Promise<unknown> => {
    const { items, wrap } = normalizeUploadResponseToItems(raw);
    if (items.length === 0) {
      return raw;
    }

    const enriched = await Promise.all(
      items.map(async item => {
        const fileId = getEntryId(item);
        if (!fileId) {
          return item;
        }
        try {
          const detail = await this.getFileById({ id: fileId });
          if (detail && typeof detail === "object") {
            return {
              ...(typeof item === "object" && item !== null
                ? (item as Record<string, unknown>)
                : {}),
              ...(detail as Record<string, unknown>),
            };
          }
        } catch {
          /* keep original upload row if GET fails */
        }
        return item;
      })
    );

    return wrap(enriched);
  };
}

export default new UploadService();
